import {
  ModalForm,
  ProFormCheckbox,
  ProFormDependency,
  ProFormSelect,
} from '@ant-design/pro-components';
import { Button, Form, Typography } from 'antd';
import PropTypes from 'prop-types';

import {
  getMetricAllocationTypeOptions,
  attributionTypeOptions,
  periodTypeOptions,
  statisticalModelTypeOptions,
} from './metric-options';
import {
  getMetricBatchLifecycleValidationError,
  getMetricLifecycleChangeValues,
} from '../utils/metric-lifecycle';

const fieldOptions = [
  { label: '生效策略', value: 'allocationType' },
  { label: '指标周期与推测模型', value: 'period' },
  { label: '归属策略', value: 'attributionType' },
];

const MetricBatchEditForm = ({ loading, metricCount, onFinish, hasSalaryMetrics = false }) => {
  const [form] = Form.useForm();
  const allocationTypeOptions = getMetricAllocationTypeOptions(
    hasSalaryMetrics ? 'salary' : 'expense',
  );

  return (
    <ModalForm
      form={form}
      omitNil={false}
      title="批量编辑指标配置"
      trigger={
        <Button type="primary" disabled={metricCount === 0}>
          批量编辑
        </Button>
      }
      width={520}
      modalProps={{ destroyOnHidden: true, okButtonProps: { loading } }}
      onFinish={onFinish}
      onValuesChange={(changedValues, values) => {
        const siblingValues = getMetricLifecycleChangeValues({
          changedValues,
          currentValues: values,
        });
        if (Object.keys(siblingValues).length > 0) {
          form.setFieldsValue(siblingValues);
        }
      }}
    >
      <Typography.Paragraph>已选择 {metricCount} 个指标</Typography.Paragraph>
      <ProFormCheckbox.Group
        name="fields"
        label="修改内容"
        options={fieldOptions}
        rules={[{ required: true, message: '请选择需要修改的指标配置' }]}
      />
      <ProFormDependency name={['fields', 'periodType']}>
        {({ fields = [], periodType }) => (
          <>
            {fields.includes('allocationType') ? (
              <ProFormSelect
                name="allocationType"
                label="生效策略"
                options={allocationTypeOptions}
                rules={[
                  { required: true, message: '请选择生效策略' },
                  {
                    validator: (_, allocationType) => {
                      if (!allocationTypeOptions.some(({ value }) => value === allocationType)) {
                        return Promise.reject(new Error('所选指标不支持当前生效策略'));
                      }
                      const error = getMetricBatchLifecycleValidationError({
                        allocationType,
                        fields,
                        periodType,
                      });
                      return error ? Promise.reject(new Error(error)) : Promise.resolve();
                    },
                  },
                ]}
                extra="仅修改生效策略，不自动修改指标周期和推测模型"
              />
            ) : null}
            {fields.includes('attributionType') && (
              <ProFormSelect
                name="attributionType"
                label="归属策略"
                options={attributionTypeOptions}
                rules={[{ required: true, message: '请选择归属策略' }]}
                extra="仅费用、工资金额指标支持；总部费用及编码 11111 的费用、工资参与分摊，其他来源仍直接归属"
              />
            )}
            {fields.includes('period') ? (
              <>
                <ProFormSelect
                  name="periodType"
                  label="指标周期"
                  options={periodTypeOptions}
                  fieldProps={{
                    allowClear: true,
                    placeholder: '不规则',
                  }}
                  extra="仅修改指标周期和推测模型，不自动修改生效策略"
                />
                {periodType ? (
                  <ProFormSelect
                    name="statisticalModelType"
                    label="推测模型"
                    options={statisticalModelTypeOptions}
                    fieldProps={{ allowClear: true, placeholder: '不启用' }}
                  />
                ) : null}
              </>
            ) : null}
          </>
        )}
      </ProFormDependency>
    </ModalForm>
  );
};

MetricBatchEditForm.propTypes = {
  hasSalaryMetrics: PropTypes.bool,
  loading: PropTypes.bool,
  metricCount: PropTypes.number.isRequired,
  onFinish: PropTypes.func.isRequired,
};

export default MetricBatchEditForm;
